import React, { useEffect, useMemo, useState } from "react";
import { FitView, fmt } from "./SlitLampFit";
import "./SimulatedFit.css";
import "./TrialLens.css";

/**
 * The trial lens to start with, drawn on the eye as it settles, and the sets
 * to pick another from.
 *
 * Every lens in each set is settled by the backend ("Simulated Fit" version 3,
 * under "trial"), its landing toricity or bi-elevation on the eye's steep
 * meridian. Each set suggests one lens per diameter: prolate unless the
 * cornea is oblate, the bi-elevation nearest the order's, and the sag that
 * settles nearest the clearance target (a shortfall counting double); on a
 * toric eye, a toric landing within 100 µm of the target over a standard one.
 * The card follows the diameter chosen in Lens Specs.
 */

const EMPTY = [];
const OBLATE_Q = 0.2;                 // as the backend's OBLATE_Q
const TORIC_CLOSE_UM = 100;           // as the backend's TORIC_CLOSE_UM
const CLEARANCE_FALLBACK = { 14.8: 225, 15.4: 225, 16.0: 275, 17.0: 275 };

const sagLabel = (sag) => (Array.isArray(sag) ? `${sag[0]}/${sag[1]}` : String(sag));
const landingLabel = (l) => (l && (l.F || l.S) ? `F${l.F}/S${l.S}` : "standard");
const power = (p) => `${p > 0 ? "+" : p < 0 ? "−" : ""}${Math.abs(p).toFixed(2)} DS`;

/** A trial lens's compact fit, filled out from its label and the report into what the drawing takes. */
export function trialFit(lens, simOption) {
  const f = lens.fit;
  if (!f) return null;
  const ordered = simOption && simOption.ordered;
  return {
    ...f,
    steep: f.quadrants.length ? f.quadrants[0].centre % 180 : null,
    sag: Array.isArray(lens.sag) ? { flat: lens.sag[0], steep: lens.sag[1] } : { single: lens.sag },
    limbal: ordered ? ordered.limbal : null,
    clearance_target: ordered ? ordered.clearance_target : CLEARANCE_FALLBACK[lens.diameter],
    dots: f.dots ? f.dots.map(([angle, x, y, clearance]) => ({ angle, x, y, clearance })) : null,
  };
}

function nearestDiameter(set, diameter) {
  const ds = [...new Set(set.lenses.map((l) => l.diameter))];
  return ds.reduce((a, b) => (Math.abs(b - diameter) < Math.abs(a - diameter) ? b : a), ds[0]);
}

function suggestionFor(set, diameter) {
  const d = nearestDiameter(set, diameter);
  return { d, s: set.suggested.find((x) => x.diameter === d) || null };
}

/** Why this lens: its profile, its bi-elevation, its sag. */
function reasons(s, lens, set, cornea) {
  if (!s) return [];
  const out = [];
  if (s.profile) {
    const name = s.profile === "oblate" ? "Oblate" : "Prolate";
    if (cornea && typeof cornea.q === "number") {
      const q = `Q ${cornea.q > 0 ? "+" : cornea.q < 0 ? "−" : ""}${Math.abs(cornea.q).toFixed(2)} over the central 8 mm`;
      const shape = cornea.q > OBLATE_Q ? "oblate" : cornea.q < 0 ? "prolate" : "close to spherical";
      out.push(`${name}: the cornea is ${shape} (${q})${shape === "close to spherical" ? `; oblate is kept for Q above +${OBLATE_Q.toFixed(2)}` : ""}.`);
    } else {
      out.push(`${name}.`);
    }
  }
  if (s.bielevation) {
    out.push(s.order_bielevation
      ? `Bi-elevation ${s.bielevation} µm, the nearest of the set's to the ${fmt(s.order_bielevation)} µm this order carries.`
      : `Bi-elevation ${s.bielevation} µm, the smaller of the set's; this order has none.`);
  }
  const off = s.clearance - s.target;
  if (s.instead_of) {
    out.push(`A toric landing for this toric eye: ${fmt(s.clearance)} µm under the lens apex, within ${TORIC_CLOSE_UM} µm of the ${s.target} µm aim. ${s.instead_of.code}, with a standard landing, settles nearer (${fmt(s.instead_of.clearance)} µm).`);
  } else if (s.clearance <= 0) {
    out.push(`Even the deepest of these rests on the cornea (${fmt(s.clearance)} µm under its apex): this eye needs more sag than ${set.name} carries at ${lens.diameter.toFixed(1)} mm.`);
  } else {
    out.push(`Its sag settles nearest the ${s.target} µm aim: ${fmt(s.clearance)} µm under the lens apex${Math.abs(off) > 100 ? `, ${fmt(Math.abs(off))} µm ${off > 0 ? "over" : "short"}, as near as this set comes` : ""}.`);
  }
  return out;
}

function Cap({ lens, on, suggested, onPick }) {
  const f = lens.fit;
  return (
    <button
      type="button"
      className={`tl-cap tl-cap-${lens.cap}${on ? " tl-cap-on" : ""}${suggested ? " tl-cap-suggested" : ""}`}
      aria-pressed={on}
      disabled={!f}
      title={f ? `${lens.diameter.toFixed(1)} ${lens.code}: ${fmt(f.clearance_under_apex)} µm under the lens apex once settled` : "Not enough of the map to settle this lens"}
      onClick={() => onPick(lens)}
    >
      <span className="tl-cap-face">
        <b>{lens.code.replace(/ BE \d+$/, " BE")}</b>
        <span>{sagLabel(lens.sag)}</span>
        <span>{lens.bc.toFixed(2)}</span>
      </span>
      <span className="tl-cap-under">{f ? `${fmt(f.clearance_under_apex)} µm` : "—"}</span>
    </button>
  );
}

function SetBrowser({ set, selected, suggestedCode, onPick }) {
  const groups = [];
  for (const lens of set.lenses) {
    const key = `${lens.profile ? `${lens.profile} · ` : ""}${lens.diameter.toFixed(1)} mm`;
    let g = groups.find((x) => x.key === key);
    if (!g) { g = { key, lenses: [] }; groups.push(g); }
    g.lenses.push(lens);
  }
  return (
    <div className="tl-browser">
      {groups.map((g) => (
        <div key={g.key} className="tl-row">
          <span className="tl-row-label">{g.key}</span>
          <div className="tl-row-caps">
            {g.lenses.map((lens) => (
              <Cap key={`${lens.diameter}${lens.code}`} lens={lens}
                   on={selected && selected.code === lens.code && selected.diameter === lens.diameter}
                   suggested={suggestedCode && suggestedCode.code === lens.code && suggestedCode.d === lens.diameter}
                   onPick={onPick} />
            ))}
          </div>
        </div>
      ))}
      <p className="tl-browser-note">Under each lens: what it leaves under its apex once settled on this eye. The ringed lens is the suggestion.</p>
    </div>
  );
}

export default function TrialLens({ sim, diameter, onLens }) {
  const ready = sim && sim.version >= 3 && sim.trial && Array.isArray(sim.trial.sets);
  const sets = ready ? sim.trial.sets : EMPTY;
  const defaultSet = useMemo(() => {
    if (!ready) return null;
    const row = (sim.trial.default || []).find((x) => x.diameter === diameter);
    return (row && row.set) || (sim.trial.sets[0] && sim.trial.sets[0].id);
  }, [ready, sim, diameter]);

  const [setId, setSetId] = useState(defaultSet);
  const [picked, setPicked] = useState(null);           // a lens chosen by hand, or null for the suggestion
  const [browsing, setBrowsing] = useState(false);
  useEffect(() => { setSetId(defaultSet); setPicked(null); }, [defaultSet, sim, diameter]);

  const set = sets.find((x) => x.id === setId) || sets[0] || null;
  const { d, s } = set ? suggestionFor(set, diameter) : { d: null, s: null };
  const suggested = s ? set.lenses.find((l) => l.code === s.code && l.diameter === d) : null;
  const lens = picked || suggested;

  // the power card can take the lens shown here as the lens on the eye
  const lensCode = lens ? lens.code : null;
  const lensDiameter = lens ? lens.diameter : null;
  const lensBc = lens ? lens.bc : null;
  const lensPower = set ? set.power : null;
  useEffect(() => {
    if (onLens) onLens(lensCode ? { code: lensCode, diameter: lensDiameter, bc: lensBc, power: lensPower } : null);
  }, [onLens, lensCode, lensDiameter, lensBc, lensPower]);
  useEffect(() => () => { if (onLens) onLens(null); }, [onLens]);

  const head = (
    <div className="sf-head">
      <h2 className="sf-title">Suggested Trial Lens</h2>
    </div>
  );
  if (!ready) {
    return (
      <div className="sf">
        {head}
        <p className="sf-note">
          {sim && sim.trial && sim.trial.error ? sim.trial.error
            : "Not available for this result: it was saved before the trial lenses were added. Upload the export again to see it."}
        </p>
      </div>
    );
  }

  const simOption = lens ? sim.options.find((o) => o.diameter === lens.diameter) : null;
  const fit = lens ? trialFit(lens, simOption) : null;
  const isSuggestion = lens && suggested && lens === suggested;

  return (
    <div className="sf tl">
      {head}
      <p className="sf-lede">
        The lens to start with from your trial set, drawn on this eye as it settles: its landing in each quadrant and where
        it decentres. Its landing toricity, or bi-elevation, sits on the eye's steep meridian.
      </p>

      <div className="tl-sets" role="group" aria-label="Trial set">
        {sets.map((x) => {
          const sx = suggestionFor(x, diameter);
          return (
            <button key={x.id} type="button" className={`tl-set${x.id === set.id ? " tl-set-on" : ""}`} aria-pressed={x.id === set.id}
                    onClick={() => { setSetId(x.id); setPicked(null); }}>
              <b>{x.name}</b>
              <small>{sx.s ? `${sx.d.toFixed(1)} ${sx.s.code} · ${fmt(sx.s.clearance)} µm` : "nothing to settle"}</small>
            </button>
          );
        })}
      </div>

      {lens ? (
        <>
          <div className="tl-pick">
            <span className={`tl-badge tl-cap-${lens.cap}`} aria-hidden="true" />
            <div className="tl-pick-text">
              <div className="tl-pick-name">
                {lens.diameter.toFixed(1)} {lens.code}
                {isSuggestion ? <span className="tl-tag">Suggested</span> : <span className="tl-tag tl-tag-own">Your pick</span>}
              </div>
              <div className="tl-pick-specs">
                {set.name} · {lens.profile ? `${lens.profile} · ` : ""}{sagLabel(lens.sag)} sag · {lens.bc.toFixed(2)} BC · landing {landingLabel(lens.landing || set.landing)} · {power(set.power)}
              </div>
              {d !== diameter && isSuggestion && (
                <p className="tl-why tl-why-warn">This set has no {diameter.toFixed(1)} mm lenses; showing its {d.toFixed(1)} mm suggestion.</p>
              )}
              {isSuggestion
                ? reasons(s, lens, set, sim.cornea).map((t) => <p key={t} className="tl-why">{t}</p>)
                : <button type="button" className="tl-back" onClick={() => setPicked(null)}>Back to the suggested lens</button>}
            </div>
            <button type="button" className="tl-browse" aria-expanded={browsing} onClick={() => setBrowsing((b) => !b)}>
              {browsing ? "Hide the set" : "Choose from the set"}
            </button>
          </div>

          {browsing && (
            <SetBrowser set={set} selected={lens} suggestedCode={s ? { code: s.code, d } : null}
                        onPick={(l) => setPicked(l === suggested ? null : l)} />
          )}

          <FitView
            title={`${lens.diameter.toFixed(1)} ${lens.code}`}
            what="settled on this eye"
            fit={fit}
            option={{ diameter: lens.diameter, lcc: simOption ? simOption.lcc : lens.diameter - 3, midperipheral_zone: simOption ? simOption.midperipheral_zone : lens.diameter - 4.6 }}
            limbus={sim.limbus_levelled}
            side={sim.eye}
            dotTarget={sim.dot_target_mm || [0, 0.5]}
          />
        </>
      ) : (
        <p className="sf-note">None of this set's lenses could be settled on this map.</p>
      )}
    </div>
  );
}
